import { useState, useEffect } from 'react';
import { API_URL } from '../../config/api';

// Original hook for single order balance (keep this!)
export const useOrderBalance = (orderNo, enabled = true) => {
  const [balanceData, setBalanceData] = useState({
    orderTotal: 0,
    totalPaid: 0,
    balanceDue: 0,
    loading: false,
    error: null
  });

  const fetchOrderBalance = async (orderNumber) => {
    if (!orderNumber || orderNumber === 'New Order') {
      return;
    }

    setBalanceData(prev => ({ ...prev, loading: true, error: null }));

    try {
      const [itemsResponse, paymentsResponse] = await Promise.all([
        fetch(`${API_URL}/api/orders/${orderNumber}/items`),
        fetch(`${API_URL}/api/orders/${orderNumber}/payments`)
      ]);

      if (!itemsResponse.ok) {
        throw new Error(`Items API error: ${itemsResponse.status}`);
      }
      
      if (!paymentsResponse.ok) {
        throw new Error(`Payments API error: ${paymentsResponse.status}`);
      }

      const [itemsData, paymentsData] = await Promise.all([
        itemsResponse.json(),
        paymentsResponse.json()
      ]);

      const items = itemsData.items || [];
      const payments = paymentsData.payments || [];

      const orderTotal = items.reduce((sum, item) => {
        return sum + parseFloat(item.OrderItemExtendedCost || 0);
      }, 0);

      const totalPaid = payments.reduce((sum, payment) => {
        return sum + parseFloat(payment.PaymentAmount || 0);
      }, 0);

      const balanceDue = orderTotal - totalPaid;

      // NEW: Find the most recent payment date
      let lastPaymentDate = null;
      if (payments.length > 0) {
        const sortedPayments = payments.sort((a, b) => {
          const dateA = new Date(a.PaymentDate || a.DateAdded);
          const dateB = new Date(b.PaymentDate || b.DateAdded);
          return dateB - dateA; // Most recent first
        });
        lastPaymentDate = sortedPayments[0].PaymentDate || sortedPayments[0].DateAdded;
      }

      setBalanceData({
        orderTotal,
        totalPaid,
        balanceDue,
        lastPaymentDate, // NEW: Add this field
        loading: false,
        error: null
      });

    } catch (error) {
      console.error('Error fetching order balance:', error);
      setBalanceData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };

  useEffect(() => {
    if (orderNo && enabled) {
      fetchOrderBalance(orderNo);
    }
  }, [orderNo, enabled]);

  return {
    ...balanceData,
    refetch: () => fetchOrderBalance(orderNo)
  };
};

// New hook for batch processing multiple order balances
export const useBatchedOrderBalances = (orders, enabled = true) => {
  const [batchData, setBatchData] = useState({
    balances: new Map(), // orderNo -> balance data
    totalBalance: 0,
    processedCount: 0,
    loading: false,
    error: null,
    progress: 0
  });

  const fetchOrderBalance = async (orderNo) => {
  try {
    const [itemsResponse, paymentsResponse] = await Promise.all([
      fetch(`${API_URL}/api/orders/${orderNo}/items`),
      fetch(`${API_URL}/api/orders/${orderNo}/payments`)
    ]);

    if (!itemsResponse.ok || !paymentsResponse.ok) {
      throw new Error(`API error for order ${orderNo}`);
    }

    const [itemsData, paymentsData] = await Promise.all([
      itemsResponse.json(),
      paymentsResponse.json()
    ]);

    const items = itemsData.items || [];
    const payments = paymentsData.payments || [];

    const orderTotal = items.reduce((sum, item) => {
      return sum + parseFloat(item.OrderItemExtendedCost || 0);
    }, 0);

    const totalPaid = payments.reduce((sum, payment) => {
      return sum + parseFloat(payment.PaymentAmount || 0);
    }, 0);

    const balanceDue = orderTotal - totalPaid;

    // NEW: Find the most recent payment date
    let lastPaymentDate = null;
    if (payments.length > 0) {
      const sortedPayments = payments.sort((a, b) => {
        const dateA = new Date(a.PaymentDate || a.DateAdded);
        const dateB = new Date(b.PaymentDate || b.DateAdded);
        return dateB - dateA; // Most recent first
      });
      lastPaymentDate = sortedPayments[0].PaymentDate || sortedPayments[0].DateAdded;
    }

    return {
      orderNo,
      orderTotal,
      totalPaid,
      balanceDue,
      lastPaymentDate, // NEW: Add this field
      error: null
    };
  } catch (error) {
    console.warn(`Failed to get balance for order ${orderNo}:`, error);
    return {
      orderNo,
      orderTotal: 0,
      totalPaid: 0,
      balanceDue: 0,
      lastPaymentDate: null, // NEW: Add this field
      error: error.message
    };
  }
};

  const processBatches = async () => {
    if (!orders || orders.length === 0) {
      setBatchData(prev => ({ ...prev, loading: false }));
      return;
    }

    setBatchData(prev => ({ 
      ...prev, 
      loading: true, 
      error: null,
      balances: new Map(),
      processedCount: 0,
      progress: 0 
    }));

    const batchSize = 5;
    const newBalances = new Map();
    let processedCount = 0;
    let totalBalance = 0;

    try {
      // Process orders in batches
      for (let i = 0; i < orders.length; i += batchSize) {
        const batch = orders.slice(i, i + batchSize);
        
        // Process current batch
        const batchPromises = batch.map(order => 
          fetchOrderBalance(order.orderno || order.orderNo || order.ID)
        );
        
        const batchResults = await Promise.all(batchPromises);
        
        // Update state with batch results
        batchResults.forEach(result => {
          newBalances.set(result.orderNo, result);
          if (!result.error) {
            totalBalance += result.balanceDue;
          }
          processedCount++;
        });

        // Update progress
        const progress = Math.round((processedCount / orders.length) * 100);
        
        setBatchData(prev => ({
          ...prev,
          balances: new Map(newBalances),
          totalBalance,
          processedCount,
          progress
        }));

        // Small delay between batches to prevent server overload
        if (i + batchSize < orders.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      setBatchData(prev => ({
        ...prev,
        loading: false
      }));

    } catch (error) {
      console.error('Error in batch processing:', error);
      setBatchData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };

  useEffect(() => {
    if (enabled && orders && orders.length > 0) {
      processBatches();
    }
  }, [orders, enabled]);

  return {
    ...batchData,
    getOrderBalance: (orderNo) => batchData.balances.get(orderNo) || null,
    refetch: processBatches
  };
};

// Individual order balance component that uses batched data
export const BatchedOrderBalanceDisplay = ({ orderNo, batchedBalances }) => {
  const balance = batchedBalances.getOrderBalance(orderNo);
  
  if (batchedBalances.loading && !balance) {
    return <span className="text-muted">Loading...</span>;
  }
  
  if (!balance || balance.error) {
    return <span className="text-warning">--</span>;
  }
  
  return (
    <span className={`balance-amount ${balance.balanceDue > 0 ? 'has-balance' : 'paid'}`}>
      {formatCurrency(Math.abs(balance.balanceDue))}
      {balance.balanceDue < 0 && <small className="text-muted ml-1">(Credit)</small>}
    </span>
  );
};

// Total balance display component
export const BatchedTotalBalanceDisplay = ({ batchedBalances }) => {
  const { totalBalance, loading, processedCount, progress, error } = batchedBalances;

  if (loading) {
    return (
      <div className="total-balance-loading">
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="progress-text">
            Processing balances... ({processedCount} completed)
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="total-balance-error">
        <div className="error-text">Error calculating total balance</div>
        <button onClick={batchedBalances.refetch} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`total-balance-display ${totalBalance > 0 ? 'has-balance' : 'no-balance'}`}>
      <div className="balance-label">Total Balance Due</div>
      <div className="balance-amount">
        {formatCurrency(Math.abs(totalBalance))}
        {totalBalance === 0 && <div className="paid-badge">All Paid Up!</div>}
        {totalBalance < 0 && <div className="credit-badge">Account Credit</div>}
      </div>
    </div>
  );
};


 export const formatCurrency = (amount) => {
    const num = parseFloat(amount || 0);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };